import * as React from "react";
import {useEffect, useRef, useState} from "react";

export const LABEL_HEIGHT = 22;

/**
 * The width of the element the ref is attached to, kept up to date when it is resized. Charts
 * draw nothing as long as the width is 0.
 */
export function useChartWidth(): [React.RefObject<HTMLDivElement | null>, number] {
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const element = containerRef.current;
        if (!element) {
            return;
        }
        const observer = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
        observer.observe(element);
        return () => observer.disconnect();
    }, []);

    return [containerRef, width];
}

/**
 * Up to about five round tick values covering the range, always including zero.
 */
export function niceTicks(minimum: number, maximum: number): number[] {
    if (minimum === 0 && maximum === 0) {
        return [0, 1];
    }
    const roughStep = (maximum - minimum) / 4;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
    const step = [1, 2, 2.5, 5, 10].map(factor => factor * magnitude).find(candidate => candidate >= roughStep) ?? roughStep;
    const ticks: number[] = [];
    for (let tick = Math.floor(minimum / step) * step; tick <= Math.ceil(maximum / step) * step + step / 2; tick += step) {
        ticks.push(Math.round(tick));
    }
    return ticks;
}

type ValueAxisProps = {
    ticks: number[]
    axisWidth: number
    width: number // of the whole chart
    y: (value: number) => number
    formatAmount: (amount: number) => string
};

/**
 * The horizontal grid lines with the amount of each tick at the left. The zero line is darker.
 */
export const ValueAxis: React.FC<ValueAxisProps> = ({ticks, axisWidth, width, y, formatAmount}) => (
    <>
        {ticks.map(tick => (
            <g key={tick}>
                <line x1={axisWidth} x2={width} y1={y(tick)} y2={y(tick)} stroke={tick === 0 ? '#9ca3af' : '#e5e7eb'} strokeWidth={1}/>
                <text x={axisWidth - 8} y={y(tick)} dy="0.32em" textAnchor="end" className="fill-gray-500 text-[11px] tabular-nums">{formatAmount(tick)}</text>
            </g>
        ))}
    </>
);

/**
 * Where the tooltip of a hovered band goes: right of the band in the left half of the chart, left
 * of it in the right half, so it stays inside the chart.
 */
export function tooltipPosition(index: number, count: number, band: number, axisWidth: number, width: number): React.CSSProperties {
    return index < count / 2
        ? {left: axisWidth + (index + 1) * band}
        : {right: width - (axisWidth + index * band)};
}
